import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import apiClient from '../api/client';

const API_BASE_URL = sessionStorage.getItem("API_BASE_URL");

function DocumentsTab({ ticketId, ticketNumber, domain }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMetadataForm, setShowMetadataForm] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false); 

  const [metadataForm, setMetadataForm] = useState({
    documentName: '',
    addedBy: '',
    addedOn: ''
  });

  const [uploadForm, setUploadForm] = useState({
    name: '',
    file: null
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDocuments();
  }, [ticketId, domain, token]);

  const fetchDocuments = async () => {
    try {
      const res = await apiClient.post(`/api/tickets/documents`, {
        domain, ticketId: Number(ticketId) });
      setDocuments(res.data || []);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMetadata = async () => {
    if (!metadataForm.documentName.trim()) {
      toast.error("Document Name is required");
      return;
    }

    try {
      await apiClient.post(`/api/tickets/${ticketId}/documents/add`, {
        ...metadataForm, domain, ticketId: Number(ticketId)
      });

      toast.success('Document metadata added');
      setShowMetadataForm(false);
      setMetadataForm({ documentName: '', addedBy: '', addedOn: '' });
      await fetchDocuments();
    } catch (err) {
      toast.error('Failed to add metadata');
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('name', uploadForm.name.trim() || uploadForm.file.name);
    formData.append('domain', domain);

    try {
      await apiClient.post(`/api/tickets/${ticketId}/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success('Document uploaded successfully');
      setShowUploadPopup(false); 
      setUploadForm({ name: '', file: null });
      await fetchDocuments(); 
    } catch (err) {
      toast.error('Upload failed');
      console.error(err.response?.data || err);
    }
  };

  const handleOpenFile = async (filePath, fileName) => {
    try {
      const res = await apiClient.get(`/api/files/open-path`, {
        params: { filePath, fileName }
      });
      window.open(res.data.url, '_blank');
    } catch (err) {
      toast.error('Failed to open file');
      console.error(err);
    }
  };

  if (loading) return <div className="text-center py-4">Loading documents...</div>;

  return (
    <div>
      <div className="mb-3">
        <button
          className="btn btn-success btn-sm me-2"
          onClick={() => setShowMetadataForm(!showMetadataForm)}
        >
          {showMetadataForm ? 'Cancel' : 'Add new record'}
        </button>

        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowUploadPopup(true)}
        >
          Upload Document
        </button>
      </div>

      {showMetadataForm && (
        <div className="bg-white p-4 border rounded mb-4 ">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Document Name *</label>
              <input
                className="form-control form-control-sm"
                placeholder="Document Name"
                value={metadataForm.documentName}
                onChange={e => setMetadataForm({ ...metadataForm, documentName: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Added By</label>
              <input
                className="form-control form-control-sm"
                placeholder="Added By"
                value={metadataForm.addedBy}
                onChange={e => setMetadataForm({ ...metadataForm, addedBy: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Added On</label>
              <DatePicker
                selected={metadataForm.addedOn ? new Date(metadataForm.addedOn) : null}
                onChange={(date) => {
                  setMetadataForm({
                    ...metadataForm,
                    addedOn: date ? format(date, 'yyyy-MM-dd') : ''
                  });
                }}
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                className="form-control form-control-sm"
                wrapperClassName="w-100"
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
          </div>
          <div className="mt-3">
            <button className="btn btn-success btn-sm me-2" onClick={handleAddMetadata}>
              Insert
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowMetadataForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showUploadPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050
          }}
          onClick={() => setShowUploadPopup(false)} 
        >
          <div 
            className="bg-white p-4 border rounded shadow-lg"
            style={{ width: '500px', maxWidth: '90%' }}
            onClick={e => e.stopPropagation()} 
          >
            <h5 className="mb-3">Upload Document</h5>

            <div className="mb-3">
              <label className="form-label small fw-semibold">Name (optional)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Enter document name"
                value={uploadForm.name}
                onChange={e => setUploadForm({ ...uploadForm, name: e.target.value })}
              />
            </div>

            <div className="mb-3">
              <label className="form-label small fw-semibold">
                File Upload <span className="text-danger">*</span>
              </label>
              <input
                type="file"
                className="form-control form-control-sm"
                onChange={e => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
              />
            </div>

            <div className="mt-4 text-end">
              <button 
                className="btn btn-secondary btn-sm me-2" 
                onClick={() => setShowUploadPopup(false)}
              >
                Close
              </button>
              <button 
                className="btn btn-success btn-sm" 
                onClick={handleUpload}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

        <table className="table table-sm table-bordered table-hover">
            <thead style={{ backgroundColor: '#0aa375', color: 'white' }}>
                <tr>
                <th style={{ fontWeight: 'normal' }}>Document Name</th>
                <th style={{ fontWeight: 'normal' }}>File Name</th>
                <th style={{ fontWeight: 'normal' }}>Added By</th>
                <th style={{ fontWeight: 'normal' }}>Added On</th>
                </tr>
            </thead>
            <tbody>
                {documents.map((doc, i) => (
                <tr key={i}>
                    <td>{doc.name || '—'}</td>

                    <td>
                    {doc.fileName ? (
                        <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleOpenFile(doc.filePath, doc.fileName);
                        }}
                        style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                        {doc.fileName}
                        </a>
                    ) : (
                        '—'
                    )}
                    </td>

                    <td>{doc.uploadedBy || '—'}</td>
                    <td>{doc.uploadedOn || '—'}</td>
                </tr>
                ))}

                {documents.length === 0 && (
                <tr>
                    <td colSpan={4} className="text-center py-3">
                    No documents found
                    </td>
                </tr>
                )}
            </tbody>
        </table>
    </div>
  );
}

export default DocumentsTab;